import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import LanguageSwitcher from './shared/LanguageSwitcher';
import '../styles/LoginScreen.css';

function LoginScreen({ onLoginSuccess, onShowTerms }) {
  const { t } = useTranslation();
  const [accessCode, setAccessCode] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [showSmsInput, setShowSmsInput] = useState(false);
  const [pendingUserId, setPendingUserId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleAccessCodeSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const data = await api.login(accessCode);

      if (data.smsVerificationRequired && !data.isOwner && !data.isAdmin) {
        setPendingUserId(data.userId);
        setShowSmsInput(true);
        setError('');
      } else {
        onLoginSuccess(data);
      }
    } catch (err) {
      setError(err.status === 401 || err.status === 403
        ? t('loginPage.alerts.accessDenied', 'Access denied. Please check your access code.')
        : 'Network error. Please try again.');
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSmsCodeSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const data = await api.verifySmsCode(pendingUserId, smsCode);
      onLoginSuccess(data);
    } catch (err) {
      setError(err.status === 401 || err.status === 403
        ? 'Verification failed. Please try again.'
        : 'Network error. Please try again.');
      console.error('SMS verification error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setShowSmsInput(false);
    setSmsCode('');
    setPendingUserId(null);
    setError('');
  };

  return (
    <div className="login-page">
      <div className="language-switcher-wrapper">
        <LanguageSwitcher />
      </div>

      <div className="logo-container">
        <h1 style={{ margin: 0, fontSize: '48px', fontWeight: 700, color: '#10b981' }}>
          Exacto
        </h1>
      </div>

      {!showSmsInput ? (
        <form className="login-form" onSubmit={handleAccessCodeSubmit}>
          <div className="input-group">
            <label className="input-label" htmlFor="access-code">
              {t('loginPageHTML.overlay.heading', 'Input your Access Code')}
            </label>
            <input
              id="access-code"
              className="input-field"
              type="password"
              value={accessCode}
              onChange={(e) => setAccessCode(e.target.value)}
              placeholder={t('loginPageHTML.overlay.form.passwordPlaceholder', 'Enter Password')}
              disabled={loading}
              autoComplete="current-password"
            />
          </div>

          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            className="submit-button"
            disabled={loading || !accessCode}
          >
            {loading ? (
              <span className="button-loading">
                <span className="spinner-small" />
                {t('loginPageHTML.overlay.form.submitButton', 'OK')}
              </span>
            ) : (
              t('loginPageHTML.overlay.form.submitButton', 'OK')
            )}
          </button>
        </form>
      ) : (
        <form className="login-form" onSubmit={handleSmsCodeSubmit}>
          <div className="sms-notice">
            <span className="sms-icon">📱</span>
            <p>Enter the verification code sent to your phone.</p>
          </div>

          <div className="input-group">
            <label className="input-label" htmlFor="sms-code">SMS Code</label>
            <input
              id="sms-code"
              className="input-field"
              type="text"
              value={smsCode}
              onChange={(e) => setSmsCode(e.target.value)}
              placeholder="SMS Code"
              disabled={loading}
              autoComplete="one-time-code"
              inputMode="numeric"
            />
          </div>

          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            className="submit-button"
            disabled={loading || !smsCode}
          >
            {loading ? (
              <span className="button-loading">
                <span className="spinner-small" />
                Verifying
              </span>
            ) : (
              'Verify'
            )}
          </button>

          <button
            type="button"
            className="back-button"
            onClick={handleBack}
            disabled={loading}
          >
            Back
          </button>
        </form>
      )}

      <div className="login-footer">
        <p className="powered-by">
          Powered by{' '}
          <a
            href="https://www.planifyit.com/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#10b981' }}
          >
            Planifyit
          </a>
        </p>
        {onShowTerms && (
          <p className="powered-by" style={{ marginTop: 8 }}>
            <button
              type="button"
              onClick={onShowTerms}
              style={{
                background: 'none',
                border: 'none',
                color: '#10b981',
                cursor: 'pointer',
                fontSize: 12,
                padding: 0,
                textDecoration: 'underline',
              }}
            >
              Terms
            </button>
          </p>
        )}
      </div>
    </div>
  );
}

export default LoginScreen;
